import React, { useContext, useEffect, useState, useRef } from 'react'
import {
  ButtonForMaterials,
  GeneralItem,
  GeneralList,
  Section,
  SectionHeader,
  SectionTitle,
} from '../style'
import { Plus, Trash2 } from 'lucide-react'
import {
  Input,
  InputDiv,
  Label,
  Option,
  SearchOptions,
} from '../../../styles/select-style'
import axios from '../../../../config/axios'
import { useSearchParams } from 'react-router-dom'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'

interface MaterialToUse {
  unitOfMeasure: 'KG' | 'MM'
  quantityToUse: string
  material: { id: number; name: string }
}

interface SelectMaterialsProps {
  stockInProduction: any[]
  integratedWithOmie: boolean
  setIsLoading: (isLoading: boolean) => void
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
}

const SelectMaterials: React.FC<SelectMaterialsProps> = ({
  stockInProduction,
  integratedWithOmie,
  setIsLoading,
  setRefresh,
}) => {
  const [materialsToUse, setMaterialsToUse] = useState<MaterialToUse[]>([])
  const [materials, setMaterials] = useState<{ id: number; name: string }[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [materialToUseIndex, setMaterialToUseIndex] = useState<number>(0)
  
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const { setFeedback } = useContext(FeedbackContext)
  const initialMaterial: MaterialToUse = {
    unitOfMeasure: 'MM',
    quantityToUse: '',
    material: { id: 0, name: '' },
  }
  const [isEditing, setIsEditing] = useState<{
    field: string
    index: number
  } | null>(null)
  const dropdownRefs = useRef<Map<string, HTMLElement | null>>(new Map())

  const isReadOnly = stockInProduction && stockInProduction.length > 0
  /* 
useEffect(() => {
    const fetchOP = async () => {
      try{
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/service/${id}`,{})
          if (response.data && response.data.orderItem && response.data.orderItem.order) {
              setOpIntegrationStatus(response.data.integratedWithOmie)
          }
      } catch(error) {
        console.log(error)
      }
    }
    fetchOP()
  }, []) */

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials`,
          {
            params: {
              search: searchTerm,
            },
          },
        )
        if (response.data) {
          const filteredMaterials = response.data.filter(
            (m: any) =>
              !materialsToUse.some(
                (m2: any) => m2.material.id === m.id && m2.material.id !== 0,
              ),
          )
          setMaterials(filteredMaterials)
        }
      } catch (error) {
        console.log(error)
      }
    }
    fetchMaterials()
  }, [materialsToUse])

  const searchMaterials = async (search: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials`,
        {
          params: {
            search: searchTerm,
          },
        },
      )
      if (response.data) {
        const filteredMaterials = response.data.filter(
          (m: any) =>
            !materialsToUse.some(
              (m2: any) => m2.material.id === m.id && m2.material.id !== 0,
            ),
        )
        setMaterials(filteredMaterials)
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (searchTerm.trim()) {
      const timeoutId = setTimeout(() => {
        searchMaterials(searchTerm)
      }, 300)

      return () => clearTimeout(timeoutId)
    } else {
      const fetchMaterials = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/raw-materials`,
          )
          if (response.data) {
            const filteredMaterials = response.data.filter(
              (m: any) =>
                !materialsToUse.some(
                  (m2: any) => m2.material.id === m.id && m2.material.id !== 0,
                ),
            )
            setMaterials(filteredMaterials)
          }
        } catch (error) {
          console.log(error)
        }
      }
      fetchMaterials()
    }
  }, [searchTerm, materialsToUse])

  useEffect(() => {
    if (isReadOnly && stockInProduction.length > 0) {
      const mappedMaterials = stockInProduction.map((item: any) => ({
        unitOfMeasure: item.usedUnitOfMeasurement || 'KG',
        quantityToUse: item.usedQuantity?.toString() || '',
        material: {
          id: item.stock?.id || 0,
          name: item.stock?.description || '',
          code: item.stock?.code.code || '',
        },
      }))
      setMaterialsToUse(mappedMaterials)
    }
  }, [stockInProduction, isReadOnly])

  const handleDiscount = async (onlyInOmie?: boolean) => {
    setIsLoading(true)
    try {
      await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/oldpcp/discount/${id}`,
        materialsToUse.map((material) => ({
          ...material,
          quantityToUse: material.quantityToUse.replace(',', '.'),
        })),
        {
          params: {
            onlyInOmie,
          },
        },
      )
    } catch (error: any) {
      console.log(error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: error.response.data.message,
      })
    }
    setRefresh((prev) => !prev)
    setIsLoading(false)
  }

  const handleEdit = (field: keyof MaterialToUse, index: number) => {
    if (isReadOnly) return
    setIsEditing({ field, index })



    if (materialToUseIndex !== index) {
      setSearchTerm('')
      setMaterialToUseIndex(index)
    }
  }

  const handleChange = (
    field: keyof MaterialToUse,
    value: { id: number; name: string } | 'KG' | 'MM',
    index: number,
  ) => {
    if (isReadOnly) return
    setMaterialsToUse((prev) =>
      prev.map((material, i) =>
        i === index ? { ...material, [field]: value } : material,
      ),
    )
    setIsEditing(null)
    if (field === 'material') {
      setSearchTerm('')
    }
  }

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (isReadOnly) return
    const value = e.target.value
    setSearchTerm(value)

    setMaterialsToUse((prev) =>
      prev.map((material, i) =>
        i === index
          ? { ...material, material: { ...material.material, name: value } }
          : material,
      ),
    )

    if (
      !isEditing ||
      isEditing.field !== 'material' ||
      isEditing.index !== index
    ) {
      setIsEditing({ field: 'material', index })
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing) {
        const target = event.target as Node
        const isClickInsideDropdown = Array.from(
          dropdownRefs.current.values(),
        ).some((ref) => ref && ref.contains(target))

        if (!isClickInsideDropdown) {
          setIsEditing(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEditing])

  const getRefKey = (field: string, index: number) => {
    return `${field}-${index}`
  }

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Selecione material(is) para a OP</SectionTitle>
      </SectionHeader>
      <GeneralList>
        {materialsToUse.map((material, index) => (
          <GeneralItem key={index}>
            <InputDiv style={{ flex: 1 }}>
              <Label>Material {index + 1}</Label>
              <Input
                type='text'
                placeholder='Digite para pesquisar material...'
                value={material.material.name}
                onChange={(e) => handleSearchChange(e, index)}
                onClick={() => handleEdit('material', index)}
                readOnly={isReadOnly}
                style={{ cursor: isReadOnly ? 'default' : 'text' }}
              />
              {!isReadOnly &&
                isEditing?.field === 'material' &&
                isEditing?.index === index && (
                  <SearchOptions
                    ref={(el) =>
                      dropdownRefs.current.set(getRefKey('material', index), el)
                    }
                  >
                    {materials.length > 0 ? (
                      materials.map((option) => (
                        <Option
                          key={option.id}
                          onClick={() =>
                            handleChange('material', option, index)
                          }
                        >
                          {option.name}
                        </Option>
                      ))
                    ) : (
                      <Option style={{ cursor: 'default', color: '#999' }}>
                        Nenhum material encontrado
                      </Option>
                    )}
                  </SearchOptions>
                )}
            </InputDiv>
            <InputDiv style={{ width: '10rem' }}>
              <Label>Unidade de medida</Label>
              <Input
                type='text'
                value={material.unitOfMeasure}
                onClick={() => handleEdit('unitOfMeasure', index)}
                readOnly={isReadOnly}
                style={{ cursor: isReadOnly ? 'default' : 'text' }}
              />
              {!isReadOnly &&
                isEditing?.field === 'unitOfMeasure' &&
                isEditing?.index === index && (
                  <SearchOptions
                    ref={(el) =>
                      dropdownRefs.current.set(
                        getRefKey('unitOfMeasure', index),
                        el,
                      )
                    }
                  >
                    <Option
                      onClick={() => handleChange('unitOfMeasure', 'MM', index)}
                    >
                      MM
                    </Option>
                    <Option
                      onClick={() => handleChange('unitOfMeasure', 'KG', index)}
                    >
                      KG
                    </Option>
                  </SearchOptions>
                )}
            </InputDiv>
            <InputDiv style={{ width: '10rem' }}>
              <Label>Qtd para usar</Label>
              <Input
                type='number'
                placeholder={`0 ${material.unitOfMeasure.toLocaleLowerCase()}`}
                min={0}
                value={material.quantityToUse}
                onChange={(e) => {
                  if (!isReadOnly) {
                    setMaterialsToUse((prev) =>
                      prev.map((material, i) =>
                        i === index
                          ? { ...material, quantityToUse: e.target.value }
                          : material,
                      ),
                    )
                  }
                }}
                readOnly={isReadOnly}
                style={{ cursor: isReadOnly ? 'default' : 'text' }}
              />
            </InputDiv>
            {!isReadOnly && (
              <Trash2
                color='#ff9090'
                size={20}
                style={{
                  cursor: 'pointer',
                  marginTop: '1rem',
                  marginLeft: '1rem',
                }}
                onClick={() =>
                  setMaterialsToUse((prev) =>
                    prev.filter((_, i) => i !== index),
                  )
                }
              />
            )}
          </GeneralItem>
        ))}
        {!isReadOnly ? (
          <GeneralItem>
            <ButtonForMaterials
              onClick={() =>
                setMaterialsToUse([...materialsToUse, initialMaterial])
              }
            >
              <Plus size={20} />
              <span>Adicionar material</span>
            </ButtonForMaterials>
            <ButtonForMaterials
              color='#ff9090'
              disabled={
                materialsToUse.length === 0 ||
                materialsToUse.some(
                  (material) =>
                    material.quantityToUse === '' || material.material.id === 0,
                )
              }
              onClick={() => handleDiscount()}
            >
              Abater no estoque
            </ButtonForMaterials>
          </GeneralItem>
        ) : (
          <>
            <ButtonForMaterials
              color={integratedWithOmie ? '#FF9C24' : '#FF9C24'}
              onClick={() => handleDiscount(true)}
              
            >
              {integratedWithOmie ? 'Reintegrar na Omie' : 'Integrar na Omie'}
            </ButtonForMaterials>
          </>
        )}
      </GeneralList>
    </Section>
  )
}

export default SelectMaterials
